import { getSessionToken } from "@/api/auth";
import AddNewDrawer from "../AddNewDrawer";
import { ComponentRenderHooks, HookTriggers } from "@/plugins/types";
import { triggerHook } from "@/plugins/client";
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { getPage, updatePage } from "@/api/pages";
import PureCanvas from "../V1Editor/PureCanvas";
import { BODY_ID, ComponentTypes, CURSOR_ID, ISettings, IStates } from "./types";
import { Loading } from "@/ui/Layout/Loading";
import { debounce, head, isEqual, keys, last, omit } from "lodash";
import TabsComponent from "@/ui/TabsComponent";
import { TabTypes } from "@/ui/TabsComponent/types";
import * as v2editorActions from "@/Apps/Pages/V2Editor/actions";
import { EditorContext } from "./EditorContext";
import { getCampaign } from "@/api/campaigns";
import { MobileState } from "../MobileResponsiveToggle/types";
import { BrowserWrapper } from "./BrowserWrapper";
import { getResponsiveSettings } from "../Page/scripts";
import { cleanThisContent } from "@/Apps/Pages/V2Editor/actions";

import { AppContext } from "@/AppContext";
import { DndProvider } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import { useCopilotWrapper } from "@/Apps/AITools/CopilotWrapper";
import { Modal } from "@/ui/Modal";
import { PopupSizes } from "@/ui/Modal/types";
import { CaretDoubleRight } from "@phosphor-icons/react";
import { mapColorsToVariables } from "../utils";
import { recursivelyRetrieveItem } from "@/Apps/Pages/V2Editor/actions";

interface FullEditorProps {
  ref?: React.Ref<any>;
  campaignId: string;
  pageId: string;
  objectId: string;
  pageContent?: ISettings[];
  mobileState?: { type: MobileState };
  setMobileState?: (state: { type: MobileState }) => void;
  sidebarSettings?: {
    floatSidebars?: boolean;
    isWidgetSidebarShowing?: boolean;
    isPropertiesSidebarShowing?: boolean;
    setIsWidgetSidebarShowing?: (isShowing: boolean) => void;
    setIsPropertiesSidebarShowing?: (isShowing: boolean) => void;
  };
  // floatSidebars?: boolean;
  // isWidgetSidebarShowing?: boolean;
  // isPropertiesSidebarShowing?: boolean;
  onLoad?: () => void;
  onChange?: ({ content }: { content: ISettings[] }) => void;
  onSave?: ({ content }: { content: ISettings[] }) => void;
}

export const FullEditor: React.FC<FullEditorProps> = forwardRef(
  (
    {
      campaignId,
      pageId,
      objectId,
      pageContent = [],
      // floatSidebars = false,
      mobileState = { type: MobileState.FULLSCREEN },
      name,
      setMobileState = () => {},
      onLoad = () => {},
      onChange = () => {},
      onSave = () => {},
      sidebarSettings = {
        floatSidebars: false,
        isWidgetSidebarShowing: true,
        isPropertiesSidebarShowing: true,
        setIsWidgetSidebarShowing: () => {},
        setIsPropertiesSidebarShowing: () => {},
      },
      // isWidgetSidebarShowing = true,
      // isPropertiesSidebarShowing = true,
    }: FullEditorProps,
    ref
  ) => {
    //  const [propertySections, setPropertySections] = useState<IPropertyBuilderSettings>();
    const [editorIsLoading, setEditorIsLoading] = useState<boolean>(true);
    const [content, setContent] = useState<ISettings[]>(pageContent);
    const [addNewDrawerType, setAddNewDrawerType] = useState<
      "OBJECTS" | "SECTIONS"
    >("OBJECTS");
    const [pageName, setPageName] = useState<string | null>(name);

    const [activeSettings, setActiveSettings] = useState({});

    const [undoStack, setUndoStack] = useState<ISettings[][]>([]);
    const [undoPosition, setUndoPosition] = useState<number>(-1);
    const [modalSettings, setModalSettings] = useState<{
      isShowing: boolean;
      title: string;
      payload: any;
      component: (props: any) => React.ReactNode;
    }>({
      isShowing: false,
      title: "Modal Title",
      payload: {},
      component: () => <div>Modal Content</div>,
    });

    const contextObj = useContext(AppContext);

    const MAX_UNDO_STACK_SIZE = 50; // Adjust as needed

    const token = getSessionToken();

    const updateEditorContent = (pageId, newContent) => {
      setContent(newContent); // update the conteont

      // update the active settings
      // const { id } = activeSettings;
      const id = activeSettings?.id;
      if (id) {
        const item = newContent.find((itm) => itm.id === id);
        setActiveSettings(item);
      }

      debouncedHandleSave({ content: newContent });
    };

    const updateEditorComponent = (componentId, newComponent) => {
      // console.log("updateEditorComponent", newComponent);
      const changedContent = v2editorActions.updateComponentSettings(
        content,
        componentId,
        newComponent
      );
      updateEditorContent(pageId, changedContent);
    };

    const updateEditorComponentStyle = (componentId, newStyle) => {
      // console.log("updateEditorComponentStyle", newStyle);
      const newComponent = v2editorActions.updateComponentStyle(
        content,
        componentId,
        newStyle
      );

      // console.log("updateEditorComponentStyle", newComponent);
      updateEditorComponent(componentId, newComponent);
    };

    const addThisAfterThat = (settings, id) => {
      const newContent = v2editorActions?.addThisAfterThat(
        content,
        settings,
        id
      );
      updateEditorContent(pageId, newContent);
    };

    const handleChange = (arg: { content: ISettings[] }) => {
      // console.log("handleChange", "arg", arg)
      onChange({ content: arg.content });

      // console.log("Change Occurred", content.filter((item) => item.html?.indexOf("Introducing") > -1));
      if (arg.content !== content) {
        setContent(arg.content);
      }

      const cleanedContent = arg.content; // cleanThisContent(content);

      addToUndoStack(cleanedContent);

      // Call the debounced save function
      debouncedHandleSave({ content: cleanedContent });
    };

    const handleItemSelect = (id, settings) => {
      console.log("Settings Passed", id, settings);
      // const allContent = recursivelyRetrieveItem(content, id);
      // Create a deep copy to avoid mutating the original content
      // const allContentCopy = JSON.parse(JSON.stringify(allContent));
      // const mappedContent = mapColorsToVariables(allContentCopy);
      // console.log('mappedContent', mappedContent);

      // get the original version of the settings
      const item = content.find((itm) => itm.id === settings?.id);
      setActiveSettings(item);
    };

    const handleChangeState = (id, state, pageContent) => {
      // console.log("handleChangeState", args);
      if (state === IStates.ACTIVE) {
        // console.log("handleChangeState", id, state, pageContent);
        const item = pageContent.find((itm) => itm.id === id);
        setActiveSettings(item);
      }
    };

    const handleShowProperties = (...args) => {
      console.log("handleShowProperties", args);
      setActiveSettings(args[0]);
    };


    const updateContext = ({
      content = [],
      pageName,
    }: {
      content: ISettings[];
      pageName: string;
    }) => {
      const { copilotContext, setCopilotContext } = contextObj;

      console.log(
        "Updating Context (for graph state)",
        pageName,
        content.slice(0, 2)
      );

      setCopilotContext({
        ...copilotContext,
        [pageName]: JSON.stringify(content),
      });
    };

    const loadPage = async () => {
      if (!campaignId) {
        setEditorIsLoading(false);
        return;
      } else {
        if (!pageId) {
          const { objects } = await getCampaign({ token, id: campaignId });
          const { page } = head(objects);
          pageId = page?.id;
        }

        const { data } = await getPage(pageId, { token });
        const { content = [], slug } = data;
        console.log("Page is loaded!", data);
        setPageName(data.name);

        setContent(cleanThisContent(content));
        // const cleanedContent = cleanThisContent(content);
        // cleanedContent.forEach((item, index) => {
        //   setTimeout(() => {
        //     setContent((prevContent) => [...prevContent, item]);
        //   }, index * 200);
        // });

        addToUndoStack(cleanThisContent(content));
        setEditorIsLoading(false);

        updateContext({
          content: cleanThisContent(content),
          pageName: data.name,
        });
      }
    };

    const handleSave = async ({ content }: { content: ISettings[] }) => {
      const cleanedContent = cleanThisContent(content);
      onSave({ content: cleanedContent });

      if (!campaignId) return;

      try {
        // purposefully not doing await, because saves happen in the background
        updatePage(pageId, {
          token,
          settings: { content: cleanedContent },
        });

        if (pageName) updateContext({ content: cleanedContent, pageName });
      } catch (error) {
        console.error("Failed to save page:", error);
        // Implement user feedback for save failure
      }
    };

    const addToUndoStack = (content: ISettings[]) => {
      // only update the stack if the incoming content is different from the content at the current position
      setUndoStack((prevStack) => {
        const cleanedContent = cleanThisContent(content);
        if (
          undoPosition === -1 ||
          !isEqual(prevStack[undoPosition], cleanedContent)
        ) {
          const newStack = [
            ...prevStack.slice(0, undoPosition + 1),
            cleanedContent,
          ].slice(-MAX_UNDO_STACK_SIZE);
          setUndoPosition(newStack.length - 1);
          return newStack;
        }
        return prevStack;
      });
    };

    const doUndo = () => {
      if (canUndo()) {
        setUndoPosition((prevPosition) => {
          const newPosition = prevPosition - 1;
          setContent(undoStack[newPosition]);
          debouncedHandleSave({ content: undoStack[newPosition] });
          return newPosition;
        });
      }
    };

    const doRedo = () => {
      console.log("doRedo was called!");
      if (canRedo()) {
        setUndoPosition((prevPosition) => {
          const newPosition = prevPosition + 1;
          setContent(undoStack[newPosition]);
          debouncedHandleSave({ content: undoStack[newPosition] });
          return newPosition;
        });
      }
    };

    const canUndo = () => undoPosition > 0;
    const canRedo = () => undoPosition < undoStack.length - 1;

    // const shouldIShowProperties =
    //   sidebarSettings.isPropertiesSidebarShowing &&
    //   Boolean(keys(activeSettings).length);
    const shouldIShowProperties = Boolean(keys(activeSettings).length);
    const rSettings = getResponsiveSettings(activeSettings, mobileState);
    const responsiveSettings = { ...rSettings, properties: omit(rSettings?.properties, "mobile") };

    useImperativeHandle(ref, () => ({
      updateEditorComponent,
      doUndo,
      doRedo,
      canUndo,
      canRedo,
      content,
    }));

    // Debounce handleSave to trigger no more than once every three seconds
    const debouncedHandleSave = useCallback(
      debounce(({ content }) => handleSave({ content }), 3000),
      []
    );

    const { artifactOptions, setArtifactOptions } = useCopilotWrapper();

    useEffect(() => {
      // console.log("Artifact Options Updated", artifactOptions);
      if (artifactOptions.newPageContent) {
        setContent(artifactOptions.newPageContent);
        setArtifactOptions({
          ...artifactOptions,
          newPageContent: undefined,
        });
      }
    }, [artifactOptions.newPageContent]);

    useEffect(() => {
      if (
        pageContent &&
        pageContent?.length &&
        JSON.stringify(cleanThisContent(pageContent)) !==
          JSON.stringify(cleanThisContent(content))
      ) {
        console.log("Page Content Updated", pageContent);
        setContent(pageContent);
      }
    }, [pageContent]);

    useEffect(() => {
      // if (!pageContent.length) loadPage();
      loadPage();
    }, []);

    if (editorIsLoading)
      return (
        <div className="p-24">
          <Loading type="gallery" />
        </div>
      );

    return (
      // <DndProvider backend={HTML5Backend}>
      <EditorContext.Provider
        value={{
          pageId,
          objectId,
          campaignId,
          content,
          mobileState,
          openModal: (payload) => {
            setModalSettings({
              isShowing: true,
              ...payload,
            });
          },
          hideModal: () => {
            setModalSettings({ isShowing: false, payload: {} });
          },
        }}
      >
        {/* <UndoTestingPanel /> */}
        {/* {JSON.stringify(artifactOptions)} */}
        {/* <div className="w-full h-full bg-white flex"> */}
        <div className="w-full h-full bg-white dark:bg-black/80 flex">

          <style>
            {`
              .wc-Body {
                background-color: #fff;
              }
            `}
          </style>

          {sidebarSettings.isWidgetSidebarShowing && (
            <div
              className={`border-r border-black/10 dark:border-white/10 h-full overflow-y-auto w-[400px] z-10 ${sidebarSettings.floatSidebars ? "absolute bg-white/90 dark:bg-black/90 backdrop-blur" : ""}`}
            >
              <div className="tabs px-7 mt-7 flex items-center justify-center mx-auto">
                <TabsComponent
                  tabs={[
                    { label: "Elements", value: "OBJECTS" },
                    { label: "Sections", value: "SECTIONS" },
                  ]}
                  activeTab={addNewDrawerType}
                  setActiveTab={setAddNewDrawerType}
                  actionSection={false}
                  type={TabTypes.BUTTON}
                />
              </div>
              <AddNewDrawer
                activeTab={addNewDrawerType}
                pageContent={content}
                activeSettings={activeSettings}
                // changeState={(id, state, clbk) => clbk()}
                withFunnelTag
                campaignId={campaignId}
                addThisAfterThat={addThisAfterThat}
              />
            </div>
          )}

          {modalSettings.isShowing && (
            <Modal
              onClose={() =>
                setModalSettings({ isShowing: false, payload: {} })
              }
              title={modalSettings.title}
              size={PopupSizes.XXXLARGE}
              footer={false}
              closeOnClickOutside
            >
              {React.createElement(modalSettings.component, {
                ...modalSettings?.payload,
                updateEditorComponent,
                hideModal: () =>
                  setModalSettings({ isShowing: false, payload: {} }),
              })}
            </Modal>
          )}

          <div
            className={`flex-1 w-0 min-w-0 h-full overflow-auto ${mobileState?.type === MobileState.FULLSCREEN ? "" : ""} text-black ${mobileState?.type === MobileState.DESKTOP && "p-9"}`}
            style={{
              // should be full width if both isWidgetSidebarShowing and shouldIShowProperties are false, but if one is showing (true) then minus 400, but if both are showing (true) then minus 800
              // maxWidth: `calc(100vw - ${sidebarSettings.isWidgetSidebarShowing && shouldIShowProperties ? "800px" : sidebarSettings.isWidgetSidebarShowing || shouldIShowProperties ? "400px" : "0px"})`,
              margin: "0 auto",
            }}
          >
            <BrowserWrapper mobileState={mobileState}>
              {/* {JSON.stringify(content)} */}

              <PureCanvas
                content={content}
                onChange={handleChange}
                onItemSelect={handleItemSelect}
                onSave={handleSave}
                disableDefaultProperties
                setActiveAddNewTab={setAddNewDrawerType}
                mobileState={mobileState}
                setMobileState={setMobileState}
                onLoad={onLoad}
                onChangeState={handleChangeState}
                onShowProperties={handleShowProperties}
              />
            </BrowserWrapper>
          </div>

          {shouldIShowProperties && (
            <div className="relative">
              <button
                onClick={() => setActiveSettings({})}
                className={`absolute top-2 z-10 border-l border-t border-b border-black/10 dark:border-white/10 bg-white/90 dark:bg-black/90 backdrop-blur dark:text-black dark:text-white text-[18px] cursor-pointer w-7 h-7 rounded flex items-center justify-center transition-colors duration-200 dark:hover:bg-black/90 hover:bg-gray-200`}
                aria-label="Close properties sidebar"
                title="Close"
                tabIndex={0}
                style={{
                  marginLeft: sidebarSettings.floatSidebars ? -425 : -30,
                }}
              >
                <CaretDoubleRight size={12} weight="bold" />
              </button>
              <div
                className={`right-0 border-black/10 dark:border-white/10 h-full overflow-y-auto w-[400px] z-10 ${sidebarSettings.floatSidebars ? "absolute bg-white/90 dark:bg-black/90 backdrop-blur" : ""}`}
              >
                <RenderProperties
                  settings={responsiveSettings}
                  rSettings={rSettings}
                  updateComponentStyle={updateEditorComponentStyle}
                  updateContent={updateEditorContent}
                  updateComponentSettings={updateEditorComponent}
                  setMobileState={setMobileState}
                  getMobileState={() => mobileState}
                  currentMobileState={mobileState}
                />
              </div>
            </div>
          )}
        </div>
      </EditorContext.Provider>
      // </DndProvider>
    );
  }
);

export const RenderProperties = ({
  settings,
  rSettings,
  updateComponentStyle,
  updateContent,
  updateComponentSettings,
  setMobileState,
  getMobileState,
  currentMobileState,
}) => {
  
  const getSettings = () => settings;

  return triggerHook(
    HookTriggers.onComponentRender,
    {
      id: ComponentRenderHooks.PROPERTIES,
      type: settings?.type,
    },
    {
      settings,
      getSettings,
      rSettings,
      updateComponentStyle,
      updateContent,
      updateComponentSettings,
      setMobileState,
      getMobileState,
      currentMobileState,
    }
  );
  
};
